import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { tenantApi } from '../../api/tenantApi';
import {
  Panel, Loader, EmptyState, useToast, errText, useCaptchaConfirm, Modal, Pagination, TenantSelect,
} from '../ui';

// Client, organisation and branch must all be selected before anything is
// fetched. Organisations / branches come from the same source as the
// Permissions page: /CPanel/GetOrganization and /CPanel/GetBranch.
// ORG_ID / BRANCH_ID below are only placeholders in the empty templates.
const ORG_ID = 0;
const BRANCH_ID = 0;
const ADMIN_USER = 'admin'; // TODO: wire to the actual logged-in admin user

// moduleId is server-generated (master DB) on create — never sent in the
// CreateModule body. See Module_Form_Permission_Sync_API_Documentation.docx §1.1.
const emptyModule = {
  organisationId: ORG_ID,
  branchId: BRANCH_ID,
  moduleName: '',
  moduleSequence: 0,
  moduleIcon: '',
  description: '',
  fineYear: '',
  createdBy: ADMIN_USER,
  isActive: true,
};

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const toList = (res) =>
  Array.isArray(res) ? res
    : Array.isArray(res?.data) ? res.data
    : Array.isArray(res?.result) ? res.result
    : [];

const pick = (obj, keys) => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

const truthy = (v) => v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';

// Accepts ModuleId / moduleId etc., so the table and the edit form always read
// the same camelCase fields no matter how the API cases them.
const normModule = (m) => {
  const active = pick(m, ['isActive', 'IsActive']);
  return {
    moduleId: pick(m, ['moduleId', 'ModuleId']),
    moduleName: pick(m, ['moduleName', 'ModuleName']) ?? '',
    moduleSequence: pick(m, ['moduleSequence', 'ModuleSequence']) ?? 0,
    moduleIcon: pick(m, ['moduleIcon', 'ModuleIcon']) ?? '',
    description: pick(m, ['description', 'Description']) ?? '',
    fineYear: pick(m, ['fineYear', 'FineYear']) ?? '',
    isActive: active === undefined ? true : truthy(active),
  };
};

export default function ModuleManagement() {
  const toast = useToast();
  const [confirm, confirmNode] = useCaptchaConfirm();

  // ---- client -> organisation -> branch ----
  const [clientCode, setClientCode] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [orgId, setOrgId] = useState('');       // strings, for <select>
  const [branchId, setBranchId] = useState('');
  const [orgLoading, setOrgLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);

  // ---- modules ----
  const [modules, setModules] = useState(null);
  const [modulesError, setModulesError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null); // null = closed; object = create/edit

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // data is fetched only after client + organisation + branch are all selected
  const ready = !!clientCode && orgId !== '' && branchId !== '';
  const effOrgId = Number(orgId);
  const effBranchId = Number(branchId);

  // 1. client chosen -> organisations (first one auto-selected)
  useEffect(() => {
    setOrgs([]); setOrgId('');
    setBranches([]); setBranchId('');
    if (!clientCode) return undefined;

    let cancelled = false;
    setOrgLoading(true);
    (async () => {
      try {
        const res = await tenantApi.getCpanelOrganizations(clientCode);
        if (cancelled) return;
        const list = toList(res);
        setOrgs(list);
        if (list.length) setOrgId(String(list[0].schoolId));
      } catch (err) {
        if (!cancelled) toast.error(errText(err));
      } finally {
        if (!cancelled) setOrgLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientCode]); // eslint-disable-line

  // 2. organisation chosen -> branches (first one auto-selected)
  useEffect(() => {
    setBranches([]); setBranchId('');
    if (!clientCode || orgId === '') return undefined;

    let cancelled = false;
    setBranchLoading(true);
    (async () => {
      try {
        const res = await tenantApi.getCpanelBranches(orgId, clientCode);
        if (cancelled) return;
        // GetBranch returns a base64 logo per branch — not needed here.
        const list = toList(res).map(({ bytLogo, ...b }) => b); // eslint-disable-line no-unused-vars
        setBranches(list);
        if (list.length) setBranchId(String(list[0].branchId));
      } catch (err) {
        if (!cancelled) toast.error(errText(err));
      } finally {
        if (!cancelled) setBranchLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientCode, orgId]); // eslint-disable-line

  // 3. organisation + branch known -> modules
  const loadSeq = useRef(0);
  const loadModules = useCallback(async () => {
    const seq = ++loadSeq.current;
    setModules(null);
    setModulesError(null);
    if (!ready) return;
    try {
      const res = await tenantApi.getModules(effOrgId, effBranchId);
      if (seq !== loadSeq.current) return;
      setModules(toList(res).map(normModule));
      setPage(1);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setModulesError(errText(err));
      toast.error(errText(err));
    }
  }, [ready, effOrgId, effBranchId]); // eslint-disable-line

  useEffect(() => { loadModules(); }, [loadModules]);

  const openCreate = () => setForm({ ...emptyModule, organisationId: effOrgId, branchId: effBranchId });
  const openEdit = (m) => setForm({ ...emptyModule, ...m, organisationId: effOrgId, branchId: effBranchId });
  // The modal can only be dismissed this way — and this is never called
  // while `busy` is true, since Modal itself is non-closable in that state.
  const closeForm = () => setForm(null);

  const submitForm = async () => {
    setBusy(true);
    try {
      if (form.moduleId) {
        // moduleId travels in the URL only — UpdateModule's documented body
        // shape doesn't repeat it.
        const { moduleId, ...body } = form;
        await tenantApi.updateModule(moduleId, body);
        toast.success('Module updated.');
      } else {
        const { moduleId, ...body } = form;
        await tenantApi.createModule(body);
        toast.success('Module created.');
      }
      closeForm();
      loadModules();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (m) => {
    const ok = await confirm(`Delete module "${m.moduleName}"? Forms under it may be orphaned — there's no cascading delete in the API.`);
    if (!ok) return;
    setBusy(true);
    try {
      await tenantApi.deleteModule(m.moduleId);
      toast.success('Module deleted.');
      loadModules();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const total = modules?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedModules = (modules || []).slice((safePage - 1) * pageSize, (safePage - 1) * pageSize + pageSize);

  const pickerLoading = orgLoading || branchLoading;

  return (
    <>
      {confirmNode}
      <h1 className="page-title">module_management</h1>
      <p className="page-sub">
        GET/POST/PUT/DELETE /api/ModuleForm/*Module — application modules per client / organisation / branch ·{' '}
        <Link to="/admin/tenants/tools/forms">manage forms →</Link>
      </p>

      <Panel title="// MODULES">
        <div className="toolbar" style={{ marginBottom: 14, gap: 10, flexWrap: 'wrap' }}>
          <div className="toolbar__group">
            <TenantSelect value={clientCode} onSelect={(cc) => setClientCode(cc)} />
          </div>

          <select
            className="term-input"
            value={orgId}
            disabled={busy || !clientCode || !orgs.length}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {!clientCode && <option value="">organisation (pick a client)</option>}
            {clientCode && !orgs.length && <option value="">No organisations</option>}
            {orgs.map((o) => (
              <option key={o.schoolId} value={String(o.schoolId)}>{o.schoolName}</option>
            ))}
          </select>

          <select
            className="term-input"
            value={branchId}
            disabled={busy || !clientCode || !branches.length}
            onChange={(e) => setBranchId(e.target.value)}
          >
            {!clientCode && <option value="">branch (pick a client)</option>}
            {clientCode && !branches.length && <option value="">No branches</option>}
            {branches.map((b) => (
              <option key={b.branchId} value={String(b.branchId)}>{b.branchName}</option>
            ))}
          </select>

          <button className="term-btn term-btn--solid term-btn--sm" onClick={openCreate} disabled={!ready || busy}>
            + new_module
          </button>
        </div>

        {!ready && pickerLoading && <Loader label="loading organisations and branches" />}
        {!ready && !pickerLoading && (
          <EmptyState>
            {!clientCode
              ? 'Select a client, organisation and branch to load modules.'
              : 'This client has no organisation or branch to load modules for.'}
          </EmptyState>
        )}

        {ready && modules === null && !modulesError && <Loader label="fetching modules" />}

        {ready && modulesError && (
          <p className="field-hint" style={{ color: 'var(--danger, #e5484d)' }}>
            getModules failed: {modulesError}
          </p>
        )}

        {ready && modules !== null && (
          <>
            <table className="term-table">
              <thead>
                <tr>
                  <th>name</th><th>seq</th><th>icon</th><th>active</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pagedModules.map((m) => (
                  <tr key={m.moduleId}>
                    <td>{m.moduleName}</td>
                    <td>{m.moduleSequence}</td>
                    <td>{m.moduleIcon}</td>
                    <td>{m.isActive ? 'yes' : 'no'}</td>
                    <td style={{ display: 'flex', gap: 6 }}>
                      <button className="term-btn term-btn--xs" onClick={() => openEdit(m)}>edit</button>
                      <button className="term-btn term-btn--xs term-btn--danger" onClick={() => handleDelete(m)}>del</button>
                    </td>
                  </tr>
                ))}
                {total === 0 && (
                  <tr><td colSpan={5} className="field-hint">No modules yet.</td></tr>
                )}
              </tbody>
            </table>

            {total > 0 && (
              <Pagination
                page={safePage}
                totalPages={totalPages}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
              />
            )}
          </>
        )}
      </Panel>

      {form && (
        <Modal
          title={form.moduleId ? `// EDIT_MODULE #${form.moduleId}` : '// NEW_MODULE'}
          closable={!busy}
          onClose={closeForm}
        >
          <div className="form-grid">
            <label>moduleName
              <input className="term-input" value={form.moduleName} disabled={busy}
                onChange={(e) => setForm({ ...form, moduleName: e.target.value })} />
            </label>
            <label>moduleSequence
              <input className="term-input" type="number" value={form.moduleSequence} disabled={busy}
                onChange={(e) => setForm({ ...form, moduleSequence: Number(e.target.value) })} />
            </label>
            <label>moduleIcon
              <input className="term-input" value={form.moduleIcon} disabled={busy}
                onChange={(e) => setForm({ ...form, moduleIcon: e.target.value })} />
            </label>
            <label>description
              <input className="term-input" value={form.description} disabled={busy}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>
            <label>fineYear
              <input className="term-input" value={form.fineYear} disabled={busy}
                onChange={(e) => setForm({ ...form, fineYear: e.target.value })} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.isActive} disabled={busy}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              isActive
            </label>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            <button className="term-btn term-btn--solid" disabled={busy} onClick={submitForm}>
              {busy ? 'saving...' : 'save'}
            </button>
            <button className="term-btn" disabled={busy} onClick={closeForm}>cancel</button>
          </div>
        </Modal>
      )}
    </>
  );
}